import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Popconfirm,
  Typography,
  Card,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  GlobalOutlined,
  PhoneOutlined,
  HomeOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { shopsApi } from '../utils/api';
import { setDeviseActive } from '../utils/devise';
import { PAYS, DEVISES } from '../constants/paysDevises';
import PlanGate from '../components/common/PlanGate';
import usePlan from '../hooks/usePlan';

const { Title } = Typography;
const { TextArea } = Input;

const ShopManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [form] = Form.useForm();

  const { canCreate } = usePlan();

  // Chargement initial des boutiques
  useEffect(() => {
    fetchShops();
  }, []);

  // Fonction pour récupérer les boutiques
  const fetchShops = async () => {
    setLoading(true);
    try {
      const response = await shopsApi.getAll({ is_active: undefined }); // Récupérer actives et inactives
      setShops(response.data || []);
    } catch (error) {
      message.error('Erreur lors du chargement des boutiques');
      console.error('Fetch shops error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Gérer l'ouverture de la modale (ajout ou modification)
  const showModal = async (shop = null) => {
    // Si c'est une nouvelle boutique, vérifier les limites du plan
    if (!shop) {
      try {
        const limitCheck = await canCreate('shops', shops.length);
        
        if (!limitCheck.allowed) {
          message.warning(
            `Limite atteinte: ${limitCheck.current}/${limitCheck.limit} boutique(s) autorisée(s) sur votre plan.`
          );
          return;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des limites:', error);
        message.error('Erreur lors de la vérification des limites');
        return;
      }
    }

    setEditingShop(shop);
    form.setFieldsValue(
      shop
        ? { ...shop }
        : { name: '', address: '', phone: '', website: '', is_active: true, country_code: 'CI', currency: 'XOF' }
    );
    setIsModalVisible(true);
  };

  /**
   * Ouvre directement le formulaire lorsqu'il est demandé depuis le sélecteur
   * de boutiques. L'URL est ensuite nettoyée pour éviter une réouverture au rechargement.
   */
  useEffect(() => {
    if (new URLSearchParams(location.search).get('create') === '1') {
      showModal();
      navigate('/account/shops', { replace: true });
    }
  }, [location.search, navigate]);

  // Gérer la fermeture de la modale
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingShop(null);
    form.resetFields();
  };

  // Gérer la soumission du formulaire (ajout ou modification)
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingShop) {
        // Mise à jour
        await shopsApi.update(editingShop.id, values);
        // Si la boutique modifiée est la boutique active, synchronise la devise d'affichage
        if (editingShop.id === window.localStorage.getItem('activeShopId')) setDeviseActive(values.currency);
        message.success('Boutique mise à jour avec succès');
      } else {
        // Création
        await shopsApi.create(values);
        message.success('Boutique créée avec succès');
        // Le marchand choisit explicitement la boutique à utiliser après sa création.
        navigate('/app/select-shop');
        return;
      }
      fetchShops(); // Recharger la liste
      handleCancel(); // Fermer la modale
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erreur lors de la sauvegarde de la boutique';
      message.error(errorMsg);
      console.error('Save shop error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Gérer la suppression d'une boutique
  const handleDelete = async (shopId) => {
    setLoading(true);
    try {
      await shopsApi.delete(shopId);
      message.success('Boutique e-commerce supprimée avec succès');
      fetchShops(); // Recharger la liste
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erreur lors de la suppression de la boutique';
      message.error(errorMsg);
      console.error('Delete shop error:', error.response?.data || error.message);
      // Explique pourquoi une boutique e-commerce avec des points POS est protégée.
      if (errorMsg.includes('points de vente')) {
          Modal.warning({
      // Conserve la portée du thème privé dans cette confirmation statique.
      rootClassName: "private-ui",
              title: 'Suppression impossible',
              content: 'Supprimez ou transférez d’abord les points de vente et leurs caissiers.',
          });
      }
    } finally {
      setLoading(false);
    }
  };

  // Définition des colonnes pour le tableau Ant Design
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Typography.Text 
          strong 
          className={window.innerWidth < 768 ? 'text-sm' : ''}
        >
          {text}
        </Typography.Text>
      ),
      width: window.innerWidth < 768 ? 120 : undefined,
    },
    {
      title: 'Adresse',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      responsive: window.innerWidth < 768 ? ['md'] : undefined,
      render: (text) => (
        <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      responsive: window.innerWidth < 768 ? ['sm'] : undefined,
      render: (text) => (
        <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Site Web',
      dataIndex: 'website',
      key: 'website',
      responsive: window.innerWidth < 768 ? ['lg'] : undefined,
      render: (text) => text ? (
        <a 
          href={text.startsWith('http') ? text : `http://${text}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}
        >
          {text}
        </a>
      ) : (
        <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>-</span>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (isActive) => (
        <Tag 
          color={isActive ? 'green' : 'red'}
          size={window.innerWidth < 768 ? 'small' : 'default'}
        >
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      width: window.innerWidth < 768 ? 70 : undefined,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: window.innerWidth < 768 ? 100 : undefined,
      render: (_, record) => (
        <div className={window.innerWidth < 768 ? 'flex flex-col gap-1' : 'flex gap-2'}>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showModal(record)}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            className={window.innerWidth < 768 ? 'min-h-[32px] text-xs' : ''}
          >
            {window.innerWidth < 768 ? 'Mod.' : 'Modifier'}
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => navigate(`/account/shops/${record.id}/team`)}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            className={window.innerWidth < 768 ? 'min-h-[32px] text-xs' : ''}
          >
            Équipe
          </Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette boutique ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
            placement={window.innerWidth < 768 ? 'top' : 'left'}
          >
            <Button 
              icon={<DeleteOutlined />} 
              danger
              size={window.innerWidth < 768 ? 'small' : 'middle'}
              className={window.innerWidth < 768 ? 'min-h-[32px] text-xs' : ''}
            >
              {window.innerWidth < 768 ? 'Supp.' : 'Supprimer'}
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className={`${window.innerWidth < 768 ? 'p-4' : 'p-6'}`}>
      <Title level={window.innerWidth < 768 ? 4 : 2} className={`${window.innerWidth < 768 ? 'text-lg mb-4' : 'text-2xl mb-6'}`}>
        <span className="hidden sm:inline">Gestion des </span>Boutiques e-commerce
      </Title>
      <Card className={window.innerWidth < 768 ? 'p-2' : ''}>
        {/* Bouton Ajouter une boutique avec contrôle de limitation */}
        <PlanGate 
          type="limit" 
          limitType="shops" 
          currentCount={shops.length}
          fallback={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled
              className={`mb-4 opacity-50 ${
                window.innerWidth < 768 ? 'w-full min-h-[44px] text-sm' : ''
              }`}
            >
              {window.innerWidth < 768 ? 'Limite atteinte' : 'Limite de boutiques atteinte'}
            </Button>
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            className={`mb-4 ${
              window.innerWidth < 768 ? 'w-full min-h-[44px] text-sm' : ''
            }`}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
          >
              {window.innerWidth < 768 ? 'Ajouter boutique' : 'Ajouter une boutique e-commerce'}
          </Button>
        </PlanGate>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={shops}
            rowKey="id"
            pagination={{
              pageSize: window.innerWidth < 768 ? 5 : 10,
              showSizeChanger: window.innerWidth > 768,
              showQuickJumper: window.innerWidth > 768,
              showTotal: (total, range) =>
                window.innerWidth > 768 
                  ? `${range[0]}-${range[1]} de ${total} boutiques`
                  : `${total} boutiques`,
            }}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            scroll={{ 
              x: 'max-content',
              scrollToFirstRowOnChange: true
            }}
          />
        </Spin>
      </Card>

      {/* Modal pour ajouter/modifier une boutique */}
      <Modal
        title={editingShop ? 'Modifier la boutique e-commerce' : 'Ajouter une boutique e-commerce'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingShop ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
        width={window.innerWidth < 768 ? '95vw' : 600}
        centered={window.innerWidth < 768}
        okButtonProps={{
          size: window.innerWidth < 768 ? 'small' : 'middle',
          className: window.innerWidth < 768 ? 'min-h-[40px]' : ''
        }}
        cancelButtonProps={{
          size: window.innerWidth < 768 ? 'small' : 'middle',
          className: window.innerWidth < 768 ? 'min-h-[40px]' : ''
        }}
      >
        <Form
          form={form}
          layout="vertical"
          name="shopForm"
        >
          <Form.Item
            label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Nom de la boutique e-commerce
            </span>}
            name="name"
            rules={[
              { required: true, message: 'Veuillez saisir le nom de la boutique' },
              { min: 2, message: 'Le nom doit contenir au moins 2 caractères' },
            ]}
          >
            <Input 
              prefix={<ShopOutlined />} 
              placeholder={window.innerWidth < 768 ? 'Nom boutique' : 'Nom de la boutique'}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Form.Item label="Pays" name="country_code" rules={[{ required: true, message: 'Sélectionnez un pays' }]}>
              <Select showSearch optionFilterProp="label" options={PAYS.map((pays) => ({ value: pays.code, label: pays.label }))} />
            </Form.Item>
            <Form.Item label="Devise" name="currency" rules={[{ required: true, message: 'Sélectionnez une devise' }]}>
              <Select showSearch optionFilterProp="label" options={DEVISES.map((devise) => ({ value: devise.code, label: devise.label }))} />
            </Form.Item>
          </div>

          <Form.Item
            label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Adresse
            </span>}
            name="address"
            rules={[
              { min: 5, message: 'L\'adresse doit contenir au moins 5 caractères' },
            ]}
          >
            <TextArea 
              placeholder={window.innerWidth < 768 ? 'Adresse boutique' : 'Adresse complète de la boutique'} 
              rows={window.innerWidth < 768 ? 2 : 3}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Téléphone
            </span>}
            name="phone"
            rules={[
              { pattern: /^[+]?[\d\s\-()]+$/, message: 'Format de téléphone invalide' },
            ]}
          >
            <Input 
              prefix={<PhoneOutlined />} 
              placeholder={window.innerWidth < 768 ? 'Téléphone' : 'Numéro de téléphone'}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Site Web
            </span>}
            name="website"
            rules={[
              { type: 'url', message: 'Veuillez saisir une URL valide' },
            ]}
          >
            <Input 
              prefix={<GlobalOutlined />} 
              placeholder={window.innerWidth < 768 ? 'www.exemple.com' : 'https://www.exemple.com'}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Statut
            </span>}
            name="is_active"
            valuePropName="checked"
          >
            <Switch 
              checkedChildren={window.innerWidth < 768 ? 'On' : 'Active'} 
              unCheckedChildren={window.innerWidth < 768 ? 'Off' : 'Inactive'}
              size={window.innerWidth < 768 ? 'small' : 'default'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShopManagement;
